import { useQuery } from '@tanstack/react-query';
import type { GuidedUiMode } from '@comp-sys/shared';
import { systemApi } from '@/lib/api/system';
import { useAuthStore } from '@/stores/auth-store';
import { isGuidedEligible } from '@/lib/utils/guided-state';

/**
 * guided 新界面对资格角色的启用模式(从 PublicFeatures 推导)。
 * 走 GET /system/features(任何登录用户可读;非 admin 也行。未启用特性后端不返回)。
 * @returns 加载中返回 undefined(调用方应等加载完再同步,避免误把用户 toggle 清掉);否则 'off'/'button'/'default'/'force'。
 */
export function useGuidedUiMode(): GuidedUiMode | undefined {
  const { data, isLoading } = useQuery({
    queryKey: ['system', 'features'],
    queryFn: systemApi.getPublicFeatures,
    // 配置变更需较快传播到 end user:60s stale + 切回标签重取 + 3 次重试(网络恢复自愈),
    // 避免永久失败时 undefined 导致 uiMode 与 admin 配置整 session 不对齐。
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
    retry: 3,
  });
  if (isLoading || !data) return undefined;
  if (!data.guidedAvailable) return 'off';
  if (data.guidedForced) return 'force';
  if (data.guidedDefault) return 'default';
  return 'button';
}

/**
 * 经典 shell 顶栏"体验新界面"按钮是否显示:
 * guided 资格角色 + admin 配置允许 guided(button 或 default 模式;off 禁用/force 永远 guided 不需按钮)。
 * default 模式下用户若已切回经典,此按钮用于回到 guided。
 */
export function useShowTryGuidedButton(): boolean {
  const guidedUiMode = useGuidedUiMode();
  const activeRole = useAuthStore((s) => s.user?.activeRole);
  return isGuidedEligible(activeRole) && (guidedUiMode === 'button' || guidedUiMode === 'default');
}
